// The dashboard's numbers (P5.1, D66), built once a night from the year's records, so the
// dashboard (P5.2) reads one small file instead of raw lists (02-DATA-MODEL query rule 3). Pure
// and unit-tested; the broker only reads the rows and stores the result. Amounts stay in their own
// currency (minor units); the dashboard converts to SAR at the month's rate when it shows them.
// Months and days are counted in Asia/Riyadh.

import type { PrStatus } from '../rules/statuses';

export interface Dims {
  entity: string;
  vertical: string;
  office: string;
  department: string;
  category: string;
}

export interface PrRec extends Dims {
  prNo: string;
  /** ISO timestamps. */
  created: string;
  status: PrStatus;
  statusAt: string;
  currency: string;
  /** Estimated budget, minor units; 0 when none was given. */
  estBudget: number;
  rush: boolean;
  deptHeadAsked: boolean;
  requesterUpn: string;
}

export interface PoRec {
  poNo: string;
  prNo: string;
  /** draft, pendingCFO, returned, rejected, sentPendingCFO, approved. */
  status: string;
  currency: string;
  /** Incl. VAT, minor units. */
  total: number;
  supplierId: string;
  supplierName: string;
  approvedAt: string;
  approvedBy: string;
  submittedAt: string;
  sentBeforeCfo: boolean;
  deliveryState: string;
  /** Value received so far incl. VAT (minor units), for partly delivered POs. */
  receivedValue: number;
}

export interface PvRec {
  pvNo: string;
  prNo: string;
  status: string;
  paidOn: string;
  amountDue: number;
  currency: string;
  returnedAt: string;
}

export interface PcRec {
  pcNo: string;
  prNo: string;
  status: string;
  reimbursedOn: string;
  amount: number;
  currency: string;
  /** For claims without a request (made before D65). */
  dims: Dims;
}

export interface QuoteRec {
  prNo: string;
  supplierId: string;
  selected: boolean;
  varianceFlag: boolean;
}

/** One month × dimensions × currency. */
export interface MonthFact extends Dims {
  /** YYYY-MM. */
  month: string;
  currency: string;
  prCount: number;
  /** Estimated budgets of the requests raised. */
  prValue: number;
  poCount: number;
  poValue: number;
  /** Of the PO value issued this month, what has been received. */
  deliveredValue: number;
  paidValue: number;
  pettyCash: number;
  /** Sum and count of days from request raised to PO issued, for the average. */
  prToPoDays: number;
  prToPoCount: number;
}

export interface SupplierFact extends Dims {
  month: string;
  currency: string;
  supplierId: string;
  supplierName: string;
  poCount: number;
  poValue: number;
}

export const STAGES = ['review', 'sourcing', 'cfo', 'issued', 'partly', 'payment'] as const;
export type Stage = (typeof STAGES)[number];

/** One open request, for the pipeline. */
export interface PipelineItem extends Dims {
  prNo: string;
  stage: Stage;
  currency: string;
  /** The PO total once there is a PO, else the estimated budget. */
  value: number;
  daysInStage: number;
}

export const EXCEPTION_KINDS = [
  'rushFewQuotes',
  'cfoOwnRequest',
  'sentToHead',
  'priceVariance',
  'sentBeforeCfo',
  'voucherReturned',
] as const;
export type ExceptionKind = (typeof EXCEPTION_KINDS)[number];

export interface ExceptionItem extends Dims {
  kind: ExceptionKind;
  month: string;
  prNo: string;
  /** PO or PV number where it applies. */
  docNo: string;
  /** Quotes received (rushFewQuotes) or days to CFO signature (sentBeforeCfo; -1 = unsigned). */
  n: number;
}

export interface Snapshot {
  version: 1;
  year: number;
  builtAt: string;
  months: MonthFact[];
  suppliers: SupplierFact[];
  pipeline: PipelineItem[];
  exceptions: ExceptionItem[];
  /** Rows read, for the health check (yearly lists stay under 20,000 items). */
  counts: { prs: number; pos: number; pvs: number; pcs: number; quotes: number };
}

const DAY = 86_400_000;
const RIYADH = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Riyadh',
  year: 'numeric',
  month: '2-digit',
});

/** YYYY-MM of an ISO timestamp in Riyadh; '' when empty or invalid. */
export function monthOf(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return RIYADH.format(d).slice(0, 7);
}

const days = (from: string, to: string): number => {
  const a = Date.parse(from);
  const b = Date.parse(to);
  return Number.isFinite(a) && Number.isFinite(b) ? Math.max(0, (b - a) / DAY) : 0;
};
const round1 = (n: number) => Math.round(n * 10) / 10;

export function stageOf(s: PrStatus): Stage | null {
  switch (s) {
    case 'ProcurementReview':
    case 'WithDeptHead':
    case 'Returned':
      return 'review';
    case 'Sourcing':
    case 'PODraft':
    case 'OnHold':
    case 'PettyCash':
      return 'sourcing';
    case 'PendingCFO':
    case 'PendingVertical':
      return 'cfo';
    case 'POSentPendingCFO':
    case 'POIssued':
      return 'issued';
    case 'PartlyReceived':
      return 'partly';
    case 'Received':
    case 'Invoiced':
    case 'VoucherWithFinance':
    case 'Paid':
      return 'payment';
    default:
      return null;
  }
}

/** A PO that went to the supplier: CFO-approved, or sent first and awaiting the signature. */
export const isIssued = (po: PoRec) => po.status === 'approved' || po.status === 'sentPendingCFO';

/** When the PO went to the supplier. */
export const issuedAt = (po: PoRec) => (po.sentBeforeCfo ? po.submittedAt : po.approvedAt);

/** What has been received of an issued PO, incl. VAT. */
export function deliveredOf(po: PoRec): number {
  if (po.deliveryState === 'full' || po.deliveryState === 'notRequired') return po.total;
  if (po.deliveryState === 'partial') return Math.min(po.total, Math.max(0, po.receivedValue));
  return 0;
}

const dimsOf = (pr: Dims): Dims => ({
  entity: pr.entity,
  vertical: pr.vertical,
  office: pr.office,
  department: pr.department,
  category: pr.category,
});
const key = (...parts: string[]) => parts.join('\u0001');

export interface BuildInput {
  year: number;
  now: Date;
  prs: readonly PrRec[];
  pos: readonly PoRec[];
  pvs: readonly PvRec[];
  pcs: readonly PcRec[];
  quotes: readonly QuoteRec[];
  /** Admin setting quotes.required (default 3). */
  quotesRequired: number;
}

export function buildSnapshot(input: BuildInput): Snapshot {
  const { prs, pos, pvs, pcs, quotes, now } = input;
  const nowIso = now.toISOString();
  const prBy = new Map(prs.map((p) => [p.prNo, p]));
  const months = new Map<string, MonthFact>();
  const fact = (month: string, dims: Dims, currency: string): MonthFact => {
    const k = key(
      month,
      dims.entity,
      dims.vertical,
      dims.office,
      dims.department,
      dims.category,
      currency,
    );
    let f = months.get(k);
    if (!f) {
      f = {
        month,
        ...dimsOf(dims),
        currency,
        prCount: 0,
        prValue: 0,
        poCount: 0,
        poValue: 0,
        deliveredValue: 0,
        paidValue: 0,
        pettyCash: 0,
        prToPoDays: 0,
        prToPoCount: 0,
      };
      months.set(k, f);
    }
    return f;
  };

  for (const pr of prs) {
    if (pr.status === 'Draft') continue;
    const m = monthOf(pr.created);
    if (!m) continue;
    const f = fact(m, pr, pr.currency);
    f.prCount += 1;
    f.prValue += pr.estBudget;
  }

  // The live PO of each request: issued ones only, the latest if a request has several.
  const issuedPos = pos.filter(isIssued);
  const poOf = new Map<string, PoRec>();
  for (const po of issuedPos) {
    const prev = poOf.get(po.prNo);
    if (!prev || issuedAt(po) > issuedAt(prev)) poOf.set(po.prNo, po);
  }

  const suppliers = new Map<string, SupplierFact>();
  for (const po of issuedPos) {
    const pr = prBy.get(po.prNo);
    const m = monthOf(issuedAt(po));
    if (!pr || !m) continue;
    const f = fact(m, pr, po.currency);
    f.poCount += 1;
    f.poValue += po.total;
    f.deliveredValue += deliveredOf(po);
    f.prToPoDays += days(pr.created, issuedAt(po));
    f.prToPoCount += 1;
    const sk = key(
      m,
      pr.entity,
      pr.vertical,
      pr.office,
      pr.department,
      pr.category,
      po.currency,
      po.supplierId,
    );
    let s = suppliers.get(sk);
    if (!s) {
      s = {
        month: m,
        ...dimsOf(pr),
        currency: po.currency,
        supplierId: po.supplierId,
        supplierName: po.supplierName,
        poCount: 0,
        poValue: 0,
      };
      suppliers.set(sk, s);
    }
    s.poCount += 1;
    s.poValue += po.total;
  }

  for (const pv of pvs) {
    if (pv.status !== 'Paid') continue;
    const pr = prBy.get(pv.prNo);
    const m = monthOf(pv.paidOn);
    if (!pr || !m) continue;
    fact(m, pr, pv.currency).paidValue += pv.amountDue;
  }

  for (const pc of pcs) {
    if (pc.status !== 'Reimbursed') continue;
    const m = monthOf(pc.reimbursedOn);
    if (!m) continue;
    const pr = pc.prNo ? prBy.get(pc.prNo) : undefined;
    fact(m, pr ?? pc.dims, pc.currency).pettyCash += pc.amount;
  }

  const pipeline: PipelineItem[] = [];
  for (const pr of prs) {
    const stage = stageOf(pr.status);
    if (!stage) continue;
    const po = poOf.get(pr.prNo);
    pipeline.push({
      prNo: pr.prNo,
      ...dimsOf(pr),
      stage,
      currency: po?.currency ?? pr.currency,
      value: po?.total ?? pr.estBudget,
      daysInStage: round1(days(pr.statusAt || pr.created, nowIso)),
    });
  }

  const exceptions: ExceptionItem[] = [];
  const add = (kind: ExceptionKind, pr: PrRec, month: string, docNo: string, n: number) =>
    exceptions.push({ kind, month, prNo: pr.prNo, docNo, n, ...dimsOf(pr) });
  const quoteSuppliers = new Map<string, Set<string>>();
  for (const q of quotes) {
    const set = quoteSuppliers.get(q.prNo) ?? new Set<string>();
    set.add(q.supplierId);
    quoteSuppliers.set(q.prNo, set);
  }
  for (const pr of prs) {
    const m = monthOf(pr.created);
    if (pr.deptHeadAsked && m) add('sentToHead', pr, m, '', 0);
    const po = poOf.get(pr.prNo);
    if (!po) continue;
    const pm = monthOf(issuedAt(po));
    const n = quoteSuppliers.get(pr.prNo)?.size ?? 0;
    if (pr.rush && n < input.quotesRequired) add('rushFewQuotes', pr, pm, po.poNo, n);
    if (po.status === 'approved' && po.approvedBy && sameUpn(po.approvedBy, pr.requesterUpn)) {
      add('cfoOwnRequest', pr, pm, po.poNo, 0);
    }
    if (po.sentBeforeCfo) {
      const signed = po.status === 'approved' && po.approvedAt;
      add(
        'sentBeforeCfo',
        pr,
        pm,
        po.poNo,
        signed ? round1(days(po.submittedAt, po.approvedAt)) : -1,
      );
    }
  }
  for (const q of quotes) {
    if (!q.selected || !q.varianceFlag) continue;
    const pr = prBy.get(q.prNo);
    const po = pr ? poOf.get(pr.prNo) : undefined;
    if (pr)
      add('priceVariance', pr, po ? monthOf(issuedAt(po)) : monthOf(pr.created), po?.poNo ?? '', 0);
  }
  for (const pv of pvs) {
    if (!pv.returnedAt) continue;
    const pr = prBy.get(pv.prNo);
    if (pr) add('voucherReturned', pr, monthOf(pv.returnedAt), pv.pvNo, 0);
  }

  const byMonth = <T extends { month: string }>(a: T, b: T) => a.month.localeCompare(b.month);
  return {
    version: 1,
    year: input.year,
    builtAt: nowIso,
    months: [...months.values()].sort(byMonth),
    suppliers: [...suppliers.values()].sort(byMonth),
    pipeline: pipeline.sort((a, b) => b.daysInStage - a.daysInStage),
    exceptions: exceptions.sort(byMonth),
    counts: {
      prs: prs.length,
      pos: pos.length,
      pvs: pvs.length,
      pcs: pcs.length,
      quotes: quotes.length,
    },
  };
}

function sameUpn(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** The Summaries list rows (one per month × dimensions × currency), D66. */
export interface SummaryRow {
  key: string;
  fields: {
    Date: string;
    Entity: string;
    Vertical: string;
    Office: string;
    Department: string;
    Category: string;
    Currency: string;
    PRCount: number;
    PRValue: number;
    POCount: number;
    POValue: number;
    DeliveredValue: number;
    PaidValue: number;
    PettyCash: number;
    PRToPODays: number;
    PRToPOCount: number;
  };
}

export const summaryKey = (f: {
  Date: string;
  Entity: string;
  Vertical: string;
  Office: string;
  Department: string;
  Category: string;
  Currency: string;
}) => key(f.Date.slice(0, 7), f.Entity, f.Vertical, f.Office, f.Department, f.Category, f.Currency);

export function summaryRows(s: Snapshot): SummaryRow[] {
  return s.months.map((m) => {
    const fields = {
      Date: `${m.month}-01T00:00:00Z`,
      Entity: m.entity,
      Vertical: m.vertical,
      Office: m.office,
      Department: m.department,
      Category: m.category,
      Currency: m.currency,
      PRCount: m.prCount,
      PRValue: m.prValue,
      POCount: m.poCount,
      POValue: m.poValue,
      DeliveredValue: m.deliveredValue,
      PaidValue: m.paidValue,
      PettyCash: m.pettyCash,
      PRToPODays: Math.round(m.prToPoDays * 10) / 10,
      PRToPOCount: m.prToPoCount,
    };
    return { key: summaryKey(fields), fields };
  });
}
